import { ProductVariantViewModel } from '@/types/api';
import { getBaseURL } from '@/features/navigation/utils/url-helpers';
import GoogleSchema from '@/components/google-schema';
import { PRODUCT_PAGE_ROUTE, UNIT_TRANSLATIONS } from './constants';
import { getPDPBreadcrumb } from './_utils/get-pdp-breadcrumb';
import { getPDPSeoValues } from './_utils/get-pdp-seo-values';
import { isRatingVisible } from './_utils/is-rating-visible';
import usePDPData from './_utils/use-pdp-data';
import { PLPPages } from '../(plp)/types';
import { PUBLIC_DOMAIN_URL } from '../_constants/urls';
const withWidth = (url?: string | null) => url ? `${url}?w=320` : undefined;
const buildOffers = (salesPrice?: number | null, isSalable?: boolean) => {
  if (!salesPrice || salesPrice <= 0) return undefined;
  return {
    '@type': 'Offer',
    priceCurrency: 'IRR',
    price: salesPrice * 10,
    availability: isSalable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    itemCondition: 'https://schema.org/NewCondition'
  };
};
const formatWeightSize = (weight?: ProductVariantViewModel['weight']) => {
  if (!weight?.value || weight.value <= 0) return '';
  const unit = weight.unit || '';
  const displayUnit = UNIT_TRANSLATIONS[unit.toLowerCase()] || unit;
  return `${weight?.value ?? ''} ${displayUnit}`.trim();
};
function Schemas() {
  const {
    data
  } = usePDPData();
  const {
    breadcrumbBrandCategory,
    breadcrumb,
    brand
  } = data || {};
  const {
    h1,
    description
  } = getPDPSeoValues(data);
  const {
    breadcrumbPath,
    breadcrumbTitle
  } = getPDPBreadcrumb(breadcrumb || [], breadcrumbBrandCategory || null, brand || null);
  const canonicalUrl = `${PUBLIC_DOMAIN_URL}/${PRODUCT_PAGE_ROUTE}/${data?.slug}`;
  const imageUrls = [data?.mainImageUrl, ...(data?.images?.map(image => image.imageUrl) || [])].map(withWidth).filter((url): url is string => !!url);
  const aggregateRating = (data?.commentsCount || 0) > 0 && isRatingVisible(data?.rate, data?.ratesCount) ? {
    '@type': 'AggregateRating',
    ratingValue: data?.rate,
    reviewCount: data?.commentsCount,
    bestRating: 5,
    worstRating: 1
  } : undefined;
  const variants = data?.variants || [];
  const isProductGroup = variants.length > 1;
  const variesByColor = variants.some(variant => !!variant.color?.id);
  const variesBySize = variants.some(variant => !!formatWeightSize(variant.weight));
  const buildVariantSchema = (variant: ProductVariantViewModel) => {
    const offers = buildOffers(variant.salesPrice, variant.isSalable);
    const image = withWidth(variant.color?.images?.[0]?.imageUrl) || withWidth(variant.color?.imageUrl) || withWidth(data?.mainImageUrl);
    const color = variant.color?.name;
    const size = formatWeightSize(variant.weight);
    const name = [h1, variesByColor && color && `رنگ ${color}`, variesBySize && size && `وزن ${size}`].filter(Boolean).join(' ');
    return {
      '@type': 'Product',
      sku: variant.id,
      name,
      ...(variesByColor && color && {
        color
      }),
      ...(variesBySize && size && {
        size
      }),
      image,
      ...(offers && {
        offers
      })
    };
  };
  const baseProductSchema = {
    '@context': 'https://schema.org',
    name: h1,
    description,
    url: canonicalUrl,
    ...(imageUrls.length > 0 && {
      image: imageUrls
    }),
    brand: {
      '@type': 'Brand',
      name: data?.brand?.nameFa
    },
    ...(aggregateRating && {
      aggregateRating
    })
  };
  const simpleProductOffers = buildOffers(data?.salesPrice, data?.isSalable);
  const productSchema = isProductGroup ? {
    ...baseProductSchema,
    '@type': 'ProductGroup',
    productGroupID: data?.id,
    variesBy: [...(variesByColor ? ['https://schema.org/color'] : []), ...(variesBySize ? ['https://schema.org/size'] : [])],
    hasVariant: variants.map(buildVariantSchema)
  } : {
    ...baseProductSchema,
    '@type': 'Product',
    sku: variants.at(0)?.id,
    ...(simpleProductOffers && {
      offers: simpleProductOffers
    })
  };
  const populatedItems = data?.breadcrumb?.map(({
    nameFa
  }, index) => {
    return {
      title: nameFa,
      url: `/${getBaseURL({
        page: PLPPages.Categories,
        basicMetas: {
          breadcrumb: data.breadcrumb?.slice(0, index + 1)
        }
      })}`
    };
  });
  const PDPBreadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: data?.brand?.nameFa,
      item: `${PUBLIC_DOMAIN_URL}/${PLPPages.Brands}/${data?.brand?.slug}`
    }, {
      '@type': 'ListItem',
      position: 2,
      name: breadcrumbTitle,
      item: `${PUBLIC_DOMAIN_URL}${breadcrumbPath}`
    }, {
      '@type': 'ListItem',
      position: 3,
      name: h1
    }]
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [...(populatedItems?.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.title,
      item: `${PUBLIC_DOMAIN_URL}${item.url}`
    })) || []), {
      '@type': 'ListItem',
      position: (populatedItems?.length || 0) + 1,
      name: h1
    }]
  };
  const MultipleBreadcrumbSchema = [breadcrumbSchema, PDPBreadcrumbSchema];
  return <>
            <GoogleSchema schema={MultipleBreadcrumbSchema} data-sentry-element="GoogleSchema" data-sentry-source-file="schemas.tsx" />
            <GoogleSchema schema={productSchema} data-sentry-element="GoogleSchema" data-sentry-source-file="schemas.tsx" />
        </>;
}
export default Schemas;